import type { ResourceState } from '@domain/schemas/contract.schema';
import type { DocumentMode } from '@helpers/document-mode';
import { get } from 'lodash-es';

import { DEFAULT_INVALID_DOCUMENT_CONFIG } from '../config/constants/defaults';
import { TRANSITION_CONFIG } from '../config/transitions';
import type { TransitionConfig, TransitionConfigFunction, TransitionContext } from '../config/types';

/**
 * Main lookup function to get transition configuration with graceful degradation
 * @param fromStatus - Current status (optional)
 * @param toStatus - Target status
 * @param documentMode - Document mode (submissions, templates, etc.)
 * @param isDocValid - Optional document validation state
 * @returns TransitionConfig or null (null means hide modal)
 */
export function getTransitionConfig(
  fromStatus: ResourceState | undefined,
  toStatus: ResourceState,
  documentMode: DocumentMode,
  isDocValid?: boolean,
  options?: Pick<TransitionContext, 'hasEndorsements'>,
): TransitionConfig | null {
  const context: TransitionContext = {
    fromStatus,
    toStatus,
    documentMode,
    hasEndorsements: options?.hasEndorsements,
  };

  const path = [fromStatus ?? '', toStatus, documentMode];
  const configFn = get(TRANSITION_CONFIG, path) as TransitionConfigFunction | undefined;

  try {
    const config = configFn?.(context);

    if (!config) {
      return null; // No configuration found - hide modal
    }

    const validationConfig = config.validation || { required: false };

    if (validationConfig.required && isDocValid === false) {
      return DEFAULT_INVALID_DOCUMENT_CONFIG(context);
    }

    return config;
  } catch {
    return null;
  }
}
